import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/utils/cn';

interface HorizontalCardCarouselProps {
  children: React.ReactNode;
  className?: string;
  gap?: string;
}

/**
 * HorizontalCardCarousel
 *
 * A reusable horizontal scrolling carousel with:
 * - Scroll snap (x mandatory, each child snaps to start)
 * - Page-width arrow scroll (scrolls by container's visible width)
 * - Mouse drag-to-scroll with grab cursor
 * - Wider gradient fades (w-20) on left/right edges when scrollable
 * - Larger arrow buttons (w-10 h-10), always visible when scrollable
 * - ResizeObserver to update scroll button visibility on resize
 * - Hidden scrollbar across all browsers (inline style + scrollbarWidth)
 *
 * Children should be `flex-none` (all existing PostCard components already are).
 */
export const HorizontalCardCarousel = ({
  children,
  className,
  gap = 'gap-3',
}: HorizontalCardCarouselProps) => {
  const { t } = useTranslation('common');

  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollStartLeft, setScrollStartLeft] = useState(0);

  const updateScrollButtons = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
  }, []);

  // Scroll event listener
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollButtons);
  }, [updateScrollButtons]);

  // ResizeObserver: update button visibility when container size changes
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      updateScrollButtons();
    });
    observer.observe(el);

    // Run once on mount
    updateScrollButtons();

    return () => observer.disconnect();
  }, [updateScrollButtons]);

  // Update when children change (new data loaded)
  useEffect(() => {
    // Use a short rAF to allow the DOM to settle after render
    const id = requestAnimationFrame(() => {
      updateScrollButtons();
    });
    return () => cancelAnimationFrame(id);
  }, [children, updateScrollButtons]);

  // Scroll by full visible width (page-width scroll)
  const handleScrollLeft = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
  };

  // Drag-to-scroll handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX);
    setScrollStartLeft(containerRef.current?.scrollLeft ?? 0);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const dx = e.pageX - startX;
    containerRef.current.scrollLeft = scrollStartLeft - dx;
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  return (
    <div className="relative">
      {/* Left gradient fade */}
      {canScrollLeft && (
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
      )}

      {/* Right gradient fade */}
      {canScrollRight && (
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />
      )}

      {/* Left Arrow */}
      {canScrollLeft && (
        <button
          onClick={handleScrollLeft}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center text-gray-600 hover:bg-white hover:text-primary-600 hover:shadow-xl hover:scale-110 transition-all duration-200"
          aria-label={t('aria_scroll_left', { defaultValue: 'Scroll left' })}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" aria-hidden="true" />
        </button>
      )}

      {/* Right Arrow */}
      {canScrollRight && (
        <button
          onClick={handleScrollRight}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center text-gray-600 hover:bg-white hover:text-primary-600 hover:shadow-xl hover:scale-110 transition-all duration-200"
          aria-label={t('aria_scroll_right', { defaultValue: 'Scroll right' })}
        >
          <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" aria-hidden="true" />
        </button>
      )}

      {/* Scrollable container — scrollbar-none hides scrollbar in all browsers */}
      {/* hcc-scroll class is used to scope the scroll-snap-align rule below */}
      <div
        ref={containerRef}
        className={cn(
          'hcc-scroll flex overflow-x-auto scroll-smooth scrollbar-none py-2 -my-2',
          gap,
          isDragging ? 'cursor-grabbing select-none' : 'cursor-grab',
          className
        )}
        style={{ scrollSnapType: 'x mandatory' } as React.CSSProperties}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {/* Apply scroll-snap-align to direct children for snap behaviour */}
      <style>{`.hcc-scroll > * { scroll-snap-align: start; flex-shrink: 0; }`}</style>
    </div>
  );
};
